/**
 * Shared Buyer SKU search box (Feb 2026).
 *
 * Extracted from SKUManagement.js's Buyer SKUs tab so Production Summary
 * + Forecast List (Demand.js) reuse the exact same behavior:
 *   • Uncontrolled text input with inline Search icon.
 *   • Enter key OR "Search" button click commits — NO debounce, NO
 *     minimum-length threshold. Matches SKU Management's pattern by
 *     design (planners already know that behavior).
 *   • A Clear button appears once a query is committed and resets both
 *     the draft state and the parent's committed value.
 *
 * Match semantics are the caller's responsibility — this component only
 * publishes the committed string. In SKU Management the string is sent
 * to the server as `?search=`; in Production Summary / Forecast List it
 * drives a client-side case-insensitive substring match against
 * `buyer_sku_id` (identical to the server's ilike behavior on the same
 * field). Same input, same commit trigger, same match logic — parity by
 * construction.
 */
import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, X } from "lucide-react";

export function BuyerSkuSearchBox({
  value = "",
  onSubmit,
  onClear,
  placeholder = "Search Buyer SKU...",
  width = "w-[220px]",
  testId,
}) {
  const [draft, setDraft] = useState(value);

  // Keep the draft in sync when the parent clears the committed value
  // (external clear-filters button, tab switch, etc.).
  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => onSubmit && onSubmit(draft.trim());
  const clear = () => {
    setDraft("");
    onClear ? onClear() : (onSubmit && onSubmit(""));
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          className={`pl-10 ${width}`}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        {value ? (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center"
            aria-label="Clear search"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      <Button variant="outline" onClick={commit} data-testid={testId ? `${testId}-submit` : undefined}>
        <Search className="h-4 w-4 mr-2" />
        Search
      </Button>
    </div>
  );
}

export default BuyerSkuSearchBox;
